import {
  AuthChannelType,
  type SessionResponse,
  type UserStoreMeFragment,
  type ValidatedUserIdentifier,
} from "@/generated/graphql";
import { useAppStore } from "@/stores/app.store";
import { useAuthStore } from "@/stores/auth/auth.store";
import { useChannelStore } from "@/stores/channel.store";
import { useTrackingStore } from "@/stores/tracking.store";
import type { UserStoreMe } from "@/stores/user.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useAuthModalPostMessage } from "./use-auth-modal-post-message";

type AuthChannelString = "otp" | "oauth";

function toAuthChannelString(channel: AuthChannelType): AuthChannelString {
  switch (channel) {
    case AuthChannelType.Otp:
      return "otp";
    case AuthChannelType.Oauth:
      return "oauth";
    default: {
      const _exhaustive: never = channel;
      return _exhaustive;
    }
  }
}

/**
 * The outcome of processing a `verifyAuthentication` response.
 * - `identified`: a known user exists; callers can close the login UI.
 * - `needs_signup`: the credentials are valid but the user has not registered;
 *    callers should open the signup flow with the provided identifiers and
 *    forward `sessionToken` so it can be echoed to the host SDK after signup.
 */
export type SessionResponseOutcome =
  | {
      status: "identified";
      sessionToken: string;
      authChannelType: AuthChannelType;
      user: UserStoreMe;
    }
  | {
      status: "needs_signup";
      sessionToken: string;
      authChannelType: AuthChannelType;
      validatedIdentifiers: ValidatedUserIdentifier[];
    };

export interface UseAuthSessionOptions {
  /**
   * Where the composable runs.
   * - `inApp`: the main frontend. Updates authStore, refreshes channel data,
   *   and fires tracking events.
   * - `standalone`: the popup auth page (`standalone-auth.page.vue`). The
   *   popup is ephemeral, so no store updates, no tracking, no channel
   *   refresh — only the host handoff via postMessage.
   */
  mode: "inApp" | "standalone";
}

/**
 * Centralizes the "what to do after a SessionResponse or signUp mutation
 * succeeds" logic shared across `login-dialog.vue`, `signup-overlay.vue`,
 * and `standalone-auth.page.vue`. Handles:
 *  - Session storage updates (in-app mode only)
 *  - Identification tracking (in-app mode only)
 *  - Channel data refresh (in-app mode only)
 *  - `auth-modal:success` postMessage handoff to the host SDK, in any
 *    context where an origin is resolvable (standalone popup, embed iframe)
 */
export function useAuthSession(options: UseAuthSessionOptions) {
  const appStore = useAppStore();
  const authStore = useAuthStore();
  const channelStore = useChannelStore();
  const trackingService = useTrackingStore();
  const { sendAuthModalMessage } = useAuthModalPostMessage();

  /**
   * Whether the current runtime context expects to hand off the session to
   * a host SDK via `auth-modal:success`. True when:
   *  - running in the standalone auth popup (mode === "standalone"), or
   *  - running embedded inside an SDK-managed iframe.
   *
   * Normal in-app login from a browser tab is NOT a handoff context, so
   * the composable skips the postMessage entirely (avoiding spurious
   * "origin parameter missing" error logs from the low-level helper).
   */
  const shouldHandoffSession = (): boolean =>
    options.mode === "standalone" || appStore.isEmbed;

  /**
   * Process a `verifyAuthentication` SessionResponse.
   *
   * In-app mode: updates `authStore`, tracks `LoginAuthenticated` on the
   * identified branch, then fires `auth-modal:success` so an embedded host
   * SDK can validate the session.
   *
   * Standalone mode: no store updates (the popup is ephemeral); still fires
   * `auth-modal:success` because that's the only reason the popup exists.
   */
  async function handleSessionResponse(
    data: SessionResponse,
    authChannel: AuthChannelType,
  ): Promise<SessionResponseOutcome> {
    const user = data.user as UserStoreMeFragment | null;

    if (options.mode === "inApp") {
      await authStore.onSessionSet({
        jwt: data.jwt,
        matchesIdentity: !!user,
        authChannelType: authChannel,
      });
    }

    if (!user) {
      return {
        status: "needs_signup",
        sessionToken: data.jwt,
        authChannelType: authChannel,
        validatedIdentifiers: data.validated_identifiers.map((identifier) => ({
          type: identifier.type,
          value: identifier.value,
          confirmation_token: identifier.confirmation_token,
        })) as ValidatedUserIdentifier[],
      };
    }

    if (options.mode === "inApp") {
      trackingService.track({
        event: TrackEventEnum.LoginAuthenticated,
        category: EventCategory.Identity,
        metadata: {
          channelId: channelStore.channel?.id ?? "",
          channelName: channelStore.channelSlug ?? "",
          userId: user.id,
          userEmail: user.email,
          userPhone: user.phone_number,
          username: user.username,
          userLocale: user.locale,
          ...channelStore.gatingExperience,
        },
      });

      await authStore.onIdentified({
        user: user as UserStoreMe,
        authChannelType: authChannel,
      });
    }

    if (shouldHandoffSession()) {
      sendAuthModalMessage({
        type: "auth-modal:success",
        sessionToken: data.jwt,
        authChannel: toAuthChannelString(authChannel),
      });
    }

    return {
      status: "identified",
      sessionToken: data.jwt,
      authChannelType: authChannel,
      user: user as UserStoreMe,
    };
  }

  /**
   * Process a successful signUp mutation result.
   *
   * In-app mode: flips the token's `matchesIdentity`, refreshes the user's
   * channel data, schedules `UserRegistered` tracking.
   *
   * Standalone mode: no local state updates.
   *
   * The `sessionToken` is optional. When provided, it's echoed to the host
   * SDK via `auth-modal:success` — this is what the in-iframe login path
   * uses so the SDK can call `validateFanfestAuth` and close its modal.
   *
   * When the signup was initiated by an SDK-pushed `Authentication` embed
   * message (via `SIGN_UP_WITH_IDENTIFIERS`), pass `sessionToken` as
   * `undefined` — the SDK already has the JWT it supplied, so echoing it
   * would trigger a redundant `/auth/fanfest-auth-validate` round-trip and
   * a surprise welcome toast.
   */
  async function handleSignupComplete(params: {
    /**
     * The newly-registered user. Required in `inApp` mode (used to update
     * the auth store and channel data). Pass `null` in `standalone` mode —
     * the popup doesn't own the identified session state, and the standalone
     * signUp mutation returns a minimal shape that isn't a full `UserStoreMe`.
     */
    user: UserStoreMe | null;
    sessionToken: string | undefined;
    authChannelType: AuthChannelType;
  }): Promise<void> {
    if (options.mode === "inApp" && params.user) {
      await authStore.onIdentified({
        user: params.user,
        authChannelType: params.authChannelType,
      });
      await channelStore.refreshUserInChannel();

      setTimeout(() => {
        trackingService.track({
          event: TrackEventEnum.UserRegistered,
          category: EventCategory.Identity,
          metadata: {
            ...channelStore.gatingExperience,
          },
        });
      }, 1000);
    }

    if (params.sessionToken && shouldHandoffSession()) {
      sendAuthModalMessage({
        type: "auth-modal:success",
        sessionToken: params.sessionToken,
        authChannel: toAuthChannelString(params.authChannelType),
      });
    }
  }

  return {
    handleSessionResponse,
    handleSignupComplete,
  };
}
